import { useEffect } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";

export function TermsOfService() {
  useEffect(() => {
    return applyPageSeo({
      title: "Terms of Service",
      description:
        "Terms of service for React Email Builder. Read the terms governing your use of our AI email template builder and related services.",
      path: "/legal/terms",
      keywords: ["terms of service", "react email builder terms", "terms and conditions"],
    });
  }, []);

  return (
    <div className="bg-white text-slate-950">
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-14 sm:py-18">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-400 transition hover:text-slate-600">Home</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span className="text-slate-700">Legal</span>
          </nav>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">Legal</p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Terms of Service</h1>
          <p className="mt-4 text-base text-slate-600">Last updated: August 8, 2026</p>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none space-y-8 text-base leading-7 text-slate-600">
            <div>
              <h2 className="text-2xl font-bold text-slate-950">1. Acceptance of Terms</h2>
              <p className="mt-3">By accessing or using React Email Builder ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. These terms apply to all visitors, users, and others who access or use the Service.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">2. Description of Service</h2>
              <p className="mt-3">React Email Builder provides an AI-powered email template builder that generates, previews, and exports email templates. The Service includes:</p>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li>A public template gallery of brand-inspired email templates</li>
                <li>An AI generation tool that creates email templates from text prompts</li>
                <li>Export functionality (HTML and React Email source code)</li>
                <li>A component library of reusable email building blocks</li>
                <li>User accounts with saved drafts and usage tracking</li>
                <li>Paid subscription features with expanded limits, when offered</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">3. Account Registration</h2>
              <p className="mt-3">To use certain features of the Service, you must create an account. You agree to:</p>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorised use of your account</li>
              </ul>
              <p className="mt-3">You must be at least 16 years old to create an account.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">4. Free & Paid Plans</h2>
              <p className="mt-3">The Service provides a free plan. A paid Pro plan may also be made available where billing is enabled:</p>
              <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-950">Feature</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-950">Free</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-950">Pro ($19/mo, when available)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr><td className="px-4 py-3 text-slate-600">AI generations</td><td className="px-4 py-3">3 / day</td><td className="px-4 py-3">200 / day</td></tr>
                    <tr><td className="px-4 py-3 text-slate-600">Exports</td><td className="px-4 py-3">5 / month</td><td className="px-4 py-3">500 / month</td></tr>
                    <tr><td className="px-4 py-3 text-slate-600">Saved drafts</td><td className="px-4 py-3">10</td><td className="px-4 py-3">300</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3">Usage limits are enforced automatically. Attempting to exceed your plan limits will result in a rate-limit response and a prompt to view available plans.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">5. Payment & Billing</h2>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li>Where paid plans are available, they are billed in advance on a monthly basis</li>
                <li>All prices are in USD unless otherwise stated</li>
                <li>Payments for paid plans are processed by Stripe — we do not store your card details</li>
                <li>Customers with an active Stripe subscription can update their payment method or manage cancellation through the Stripe Customer Portal</li>
                <li>Cancellation timing and any refund terms are shown at checkout and in the Stripe Customer Portal</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">6. Refund Policy</h2>
              <p className="mt-3">Any voluntary refund period or other refund terms will be stated at checkout. Unless otherwise stated at purchase, charges are non-refundable once paid. This does not affect any refund or cancellation rights you have under applicable law.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">7. Intellectual Property</h2>
              <h3 className="mt-6 text-lg font-semibold text-slate-950">7.1 Our Content</h3>
              <p className="mt-3">The React Email Builder platform, including its design, code, branding, and the template gallery, is owned by Subcode Ventures Ltd. The brand-inspired templates are our original creations inspired by common email patterns — they are not official templates from the referenced brands.</p>
              <h3 className="mt-6 text-lg font-semibold text-slate-950">7.2 Your Content</h3>
              <p className="mt-3">As between you and us, we do not claim ownership of email templates you create or generate using the Service. To the extent rights in generated content can be owned under applicable law, and subject to any third-party rights, you may use that content in commercial projects without attribution to us. You may not use generated templates to impersonate any brand.</p>
              <h3 className="mt-6 text-lg font-semibold text-slate-950">7.3 Brand References</h3>
              <p className="mt-3">Brand names used in template titles and descriptions (e.g., "Airbnb-inspired", "Stripe-style") are for descriptive and educational purposes only. We are not affiliated with, endorsed by, or connected to any of the referenced brands. All brand names and trademarks are the property of their respective owners.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">8. Acceptable Use</h2>
              <p className="mt-3">You agree not to use the Service to:</p>
              <ul className="mt-3 ml-6 list-disc space-y-2">
                <li>Generate spam, phishing, or fraudulent emails</li>
                <li>Impersonate any brand, organisation, or individual</li>
                <li>Violate any applicable law or regulation</li>
                <li>Attempt to circumvent usage limits or security measures</li>
                <li>Reverse-engineer, decompile, or disrupt the Service</li>
                <li>Use automated tools to scrape or mass-download templates</li>
                <li>Share your account credentials with others</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">9. AI-Generated Content</h2>
              <p className="mt-3">AI-generated email templates are created using third-party AI models (Google Gemini). While we strive for quality and accuracy, we do not guarantee that AI-generated content will be error-free, suitable for any particular purpose, or free from bias. You are responsible for reviewing and testing any AI-generated content before sending it to recipients.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">10. Disclaimer of Warranties</h2>
              <p className="mt-3">The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, secure, or error-free. We do not warrant the accuracy, completeness, or usefulness of AI-generated content.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">11. Limitation of Liability</h2>
              <p className="mt-3">To the maximum extent permitted by law, Subcode Ventures Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of the Service, even if we have been advised of the possibility of such damages. Our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">12. Indemnification</h2>
              <p className="mt-3">You agree to indemnify and hold harmless Subcode Ventures Ltd from any claims, damages, or expenses arising from your use of the Service, your violation of these terms, or your violation of any rights of a third party.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">13. Termination</h2>
              <p className="mt-3">We may suspend or terminate your account at any time for violation of these terms or for any other reason at our discretion. Upon termination, your right to use the Service ceases immediately. Provisions that by their nature should survive termination (IP, disclaimers, liability, indemnification) will remain in effect.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">14. Governing Law</h2>
              <p className="mt-3">These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">15. Changes to Terms</h2>
              <p className="mt-3">We may update these terms from time to time. We will publish the updated terms with a revised “Last updated” date. Where required by law, we will provide additional notice before material changes take effect. Continued use of the Service after changes take effect constitutes acceptance of the updated terms.</p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-950">16. Contact</h2>
              <p className="mt-3">If you have questions about these terms, contact us at:</p>
              <p className="mt-2"><a href="mailto:legal@reactemailbuilder.com" className="text-sky-700 underline hover:text-sky-600">legal@reactemailbuilder.com</a></p>
              <p className="mt-2">Subcode Ventures Ltd<br />Kernow House, Gas Hill, Newham, Truro, TR1 2XP, United Kingdom</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
